import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import RemoveCircleOutlineOutlinedIcon from "@mui/icons-material/RemoveCircleOutlineOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { getLocationsByCustomerId } from "../../api/location";

const JOB_STATUS_OPTIONS = [
  "pending",
  "assigned",
  "in_progress",
  "completed",
  "failed",
  "cancelled",
];

const isJobStatusAllowed = (status, hasAssignedTechnician) =>
  hasAssignedTechnician ? status !== "pending" : status === "pending";

const JOB_PRIORITY_OPTIONS = ["low", "medium", "high", "urgent"];

const TASK_STATUS_OPTIONS = [
  "pending",
  "in_progress",
  "completed",
  "failed",
  "skipped",
  "not_applicable",
];

const toInputDateTime = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const formatAuditDateTime = (value) => {
  if (!value) return "Unknown";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return date.toLocaleString([], {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const createEditTaskDraft = (task = {}) => ({
  task_id: task.task_id || "",
  task_name: task.task_name || "",
  is_new: false,
  status: task.status || "pending",
  notes: task.notes || task.task_notes || "",
  parts_used: task.parts_used || "",
  failure_reason: task.failure_reason || "",
  time_spent_minutes:
    task.time_spent_minutes === null || task.time_spent_minutes === undefined
      ? ""
      : String(task.time_spent_minutes),
});

const normalizeTechnicianValue = (value, technicianOptions = []) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const normalized = String(value);
  const exists = technicianOptions.some(
    (option) => String(option.value) === normalized,
  );

  return exists ? normalized : "";
};

const createEditJobDraft = (job = null, technicianOptions = []) => {
  const assignedById =
    job?.assigned_technician_id === null ||
    job?.assigned_technician_id === undefined
      ? ""
      : String(job.assigned_technician_id);

  const assignedByName = String(job?.assigned_technician_name || "")
    .trim()
    .toLowerCase();

  const matchedByName = assignedByName
    ? technicianOptions.find(
        (option) =>
          String(option.label || "")
            .trim()
            .toLowerCase() === assignedByName,
      )
    : null;

  const assignedTechnicianId =
    assignedById || String(matchedByName?.value || "");

  return {
    job_number: job?.job_number || "",
    customer_name: job?.customer_name || job?.customer_id || "",
    status: job?.status || "pending",
    priority: job?.priority || "medium",
    assigned_technician_id: normalizeTechnicianValue(
      assignedTechnicianId,
      technicianOptions,
    ),
    assigned_technician_name:
      job?.assigned_technician_name || job?.assigned_technician_id || "",
    service_type_name: job?.service_type_name || job?.service_type_id || "",
    device_name: job?.device_name || job?.device_id || "",
    scheduled_start: toInputDateTime(job?.scheduled_start),
    scheduled_end: toInputDateTime(job?.scheduled_end),
    external_notes: job?.external_notes || "",
    internal_notes: job?.internal_notes || "",
    cancellation_reason: job?.cancellation_reason || "",
    tasks:
      Array.isArray(job?.tasks) && job.tasks.length > 0
        ? job.tasks.map((task) => createEditTaskDraft(task))
        : [],
  };
};

const createNewTaskDraft = () => ({
  is_new: true,
  service_id: "",
  task_id: "",
  task_name: "",
  estimated_duration: "",
  priority: "medium",
  description: "",
  notes: "",
  parts_used: "",
  failure_reason: "",
  status: "pending",
});

const isCustomerWithId = (user) =>
  user?.role === "customer" && user?.customerPkId ? true : false;

const createNewJobDraft = (
  user = null,
  customerPkId = "",
  defaultScheduledStart = "",
) => ({
  customer_id: customerPkId,
  service_type_id: isCustomerWithId(user) ? 1 : "",
  device_id: "",
  location_id: isCustomerWithId(user) ? String(user.baseLocationId) : "",
  department_id: "",
  room_id: "",
  scheduled_start: defaultScheduledStart || toInputDateTime(new Date()),
  scheduled_end: "",
  assigned_technician_id: "",
  status: "pending",
  priority: "medium",
  external_notes: "",
  internal_notes: "",
  created_by: user?.pk_id || user?.id || "",
  tasks: [createNewTaskDraft()],
});

const shallowEqual = (left, right) =>
  JSON.stringify(left || {}) === JSON.stringify(right || {});

const buildTaskSummary = (tasks = []) => {
  const summary = {
    total: 0,
    pending: 0,
    in_progress: 0,
    completed: 0,
    failed: 0,
    other: 0,
  };

  if (!Array.isArray(tasks)) {
    return summary;
  }

  summary.total = tasks.length;
  tasks.forEach((task) => {
    const status = String(task?.status || "pending").toLowerCase();
    if (status === "pending") summary.pending += 1;
    else if (status === "in_progress") summary.in_progress += 1;
    else if (status === "completed") summary.completed += 1;
    else if (status === "failed") summary.failed += 1;
    else summary.other += 1;
  });

  return summary;
};

export default function JobsInspector({
  mode = "view",
  editable = false,
  job,
  onCreateJob,
  onUpdateJob,
  onRequestClose,
  onDirtyChange,
  taskMetadata,
  technicianOptions = [],
  devices = [],
  customers = [],
  user,
  customerPkId,
  defaultScheduledStart = "",
  customerContext = null,
}) {
  const isCreateMode = mode === "create";
  const [errorMessage, setErrorMessage] = useState("");
  const [saveBusy, setSaveBusy] = useState(false);
  const [locationOptions, setLocationOptions] = useState([]);
  const [editOriginal, setEditOriginal] = useState(
    createEditJobDraft(job, technicianOptions),
  );
  const [editDraft, setEditDraft] = useState(
    createEditJobDraft(job, technicianOptions),
  );
  const [createOriginal, setCreateOriginal] = useState(
    createNewJobDraft(user, customerPkId, defaultScheduledStart),
  );
  const [createDraft, setCreateDraft] = useState(
    createNewJobDraft(user, customerPkId, defaultScheduledStart),
  );
  const [taskEditorIndex, setTaskEditorIndex] = useState(null);
  const [notesTab, setNotesTab] = useState("external");
  const reportedDirtyRef = useRef(null);

  const isCustomerWithIdUser = isCustomerWithId(user);
  const isAdminUser = String(user?.role || "").toLowerCase() === "admin";
  const assignedTechnicianId =
    editDraft.assigned_technician_id || job?.assigned_technician_id || "";
  const assignedTechnicianName =
    editDraft.assigned_technician_name || job?.assigned_technician_name || "";
  const hasAssignedTechnician = Boolean(
    assignedTechnicianId || assignedTechnicianName.trim(),
  );

  useEffect(() => {
    const internalTabUnavailable = !isAdminUser && notesTab === "internal";
    const failureTabUnavailable = isCreateMode && notesTab === "cancellation";
    if (internalTabUnavailable || failureTabUnavailable) {
      setNotesTab("external");
    }
  }, [isAdminUser, isCreateMode, notesTab]);

  useEffect(() => {
    if (!isCreateMode) {
      const next = createEditJobDraft(job, technicianOptions);
      setEditOriginal(next);
      setEditDraft(next);
      setErrorMessage("");
    }
  }, [isCreateMode, job, technicianOptions]);

  useEffect(() => {
    if (isCreateMode) {
      const next = createNewJobDraft(user, customerPkId, defaultScheduledStart);
      setCreateOriginal(next);
      setCreateDraft(next);
      setErrorMessage("");
    }
  }, [customerPkId, defaultScheduledStart, isCreateMode, user]);

  useEffect(() => {
    if (!isCreateMode) {
      return;
    }

    const customerId = String(createDraft.customer_id || "");
    if (!customerId) {
      setLocationOptions([]);
      return;
    }

    let mounted = true;
    getLocationsByCustomerId(customerId)
      .then((response) => {
        if (!mounted) return;
        setLocationOptions(Array.isArray(response?.data) ? response.data : []);
      })
      .catch((err) => {
        if (!mounted) return;
        setLocationOptions([]);
        setErrorMessage(err?.message || "Failed to load locations");
      });

    return () => {
      mounted = false;
    };
  }, [createDraft.customer_id, isCreateMode]);

  const serviceOptions = useMemo(() => {
    return taskMetadata?.taskMetaData?.service_types || [];
  }, [taskMetadata]);

  const servicesByType = useMemo(() => {
    return taskMetadata?.taskMetaData?.grouped?.by_service_type || {};
  }, [taskMetadata]);

  const tasksByService = useMemo(() => {
    return taskMetadata?.taskMetaData?.grouped?.by_service || {};
  }, [taskMetadata]);

  const filteredCreateDevices = useMemo(() => {
    if (!createDraft.customer_id || !createDraft.location_id) {
      return [];
    }

    return (Array.isArray(devices) ? devices : []).filter((device) => {
      const customerId = String(
        device.customer_pk_id || device.customer_id || "",
      );
      const locationId = String(
        device.location_pk_id ||
          device.location_id ||
          device.device_location_id ||
          "",
      );

      return (
        customerId === String(createDraft.customer_id) &&
        locationId === String(createDraft.location_id)
      );
    });
  }, [createDraft.customer_id, createDraft.location_id, devices]);

  const createTaskOptions = useMemo(() => {
    if (!createDraft.service_type_id || !createDraft.device_id) {
      return [];
    }

    const selectedDevice = filteredCreateDevices.find(
      (device) => String(device.pk_id) === String(createDraft.device_id),
    );
    const selectedDeviceId = String(
      selectedDevice?.pk_id || createDraft.device_id,
    )
      .trim()
      .toLowerCase();
    const selectedDeviceName = String(
      selectedDevice?.deviceName || selectedDevice?.name || "",
    )
      .trim()
      .toLowerCase();

    const matchesDevice = (taskItem) => {
      const taskDeviceIds = [
        ...(Array.isArray(taskItem?.device_ids) ? taskItem.device_ids : []),
        ...(Array.isArray(taskItem?.allowed_device_ids)
          ? taskItem.allowed_device_ids
          : []),
        ...(Array.isArray(taskItem?.compatible_device_ids)
          ? taskItem.compatible_device_ids
          : []),
        taskItem?.device_id,
      ]
        .filter(Boolean)
        .map((value) => String(value).trim().toLowerCase());

      const taskDeviceNames = [
        ...(Array.isArray(taskItem?.device_names) ? taskItem.device_names : []),
        taskItem?.device_name,
      ]
        .filter(Boolean)
        .map((value) => String(value).trim().toLowerCase());

      const hasDeviceConstraint =
        taskDeviceIds.length > 0 || taskDeviceNames.length > 0;

      if (!hasDeviceConstraint) {
        return true;
      }

      if (selectedDeviceId && taskDeviceIds.includes(selectedDeviceId)) {
        return true;
      }

      if (selectedDeviceName && taskDeviceNames.includes(selectedDeviceName)) {
        return true;
      }

      return false;
    };

    const serviceEntries =
      servicesByType[createDraft.service_type_id]?.services || [];
    const selectedServiceTasks = serviceEntries.flatMap((service) => {
      const serviceId = String(service.pk_id || "");
      return tasksByService[serviceId]?.task || [];
    });

    return selectedServiceTasks
      .filter(matchesDevice)
      .map((taskItem) => ({
        id: String(taskItem.pk_id),
        name: taskItem.task_name || "",
      }))
      .filter((item) => item.id && item.name)
      .sort((left, right) => left.name.localeCompare(right.name));
  }, [
    createDraft.device_id,
    createDraft.service_type_id,
    filteredCreateDevices,
    servicesByType,
    tasksByService,
  ]);

  const editTaskOptions = useMemo(() => {
    if (isCreateMode) {
      return [];
    }

    const serviceTypeRaw =
      job?.service_type_id ||
      editDraft.service_type_name ||
      job?.service_type_name ||
      "";
    const serviceTypeText = String(serviceTypeRaw).trim().toLowerCase();

    let serviceTypeId = null;
    for (const candidate of serviceOptions) {
      const candidateId = String(candidate.id || "")
        .trim()
        .toLowerCase();
      const candidateName = String(candidate.name || "")
        .trim()
        .toLowerCase();
      if (
        serviceTypeText &&
        (candidateId === serviceTypeText || candidateName === serviceTypeText)
      ) {
        serviceTypeId = String(candidate.id);
        break;
      }
    }

    const serviceEntries = serviceTypeId
      ? servicesByType[serviceTypeId]?.services || []
      : [];

    const currentDeviceId = String(job?.device_id || "")
      .trim()
      .toLowerCase();
    const currentDeviceName = String(
      job?.device_name || editDraft.device_name || "",
    )
      .trim()
      .toLowerCase();

    const matchesDevice = (taskItem) => {
      const taskDeviceIds = [
        ...(Array.isArray(taskItem?.device_ids) ? taskItem.device_ids : []),
        ...(Array.isArray(taskItem?.allowed_device_ids)
          ? taskItem.allowed_device_ids
          : []),
        ...(Array.isArray(taskItem?.compatible_device_ids)
          ? taskItem.compatible_device_ids
          : []),
        taskItem?.device_id,
      ]
        .filter(Boolean)
        .map((value) => String(value).trim().toLowerCase());

      const taskDeviceNames = [
        ...(Array.isArray(taskItem?.device_names) ? taskItem.device_names : []),
        taskItem?.device_name,
      ]
        .filter(Boolean)
        .map((value) => String(value).trim().toLowerCase());

      const hasDeviceConstraint =
        taskDeviceIds.length > 0 || taskDeviceNames.length > 0;

      if (!hasDeviceConstraint) {
        return true;
      }

      if (currentDeviceId && taskDeviceIds.includes(currentDeviceId)) {
        return true;
      }

      if (currentDeviceName && taskDeviceNames.includes(currentDeviceName)) {
        return true;
      }

      return false;
    };

    const taskMap = new Map();
    serviceEntries.forEach((service) => {
      const serviceId = String(service.pk_id || "");
      if (!serviceId) {
        return;
      }

      const tasks = tasksByService[serviceId]?.task || [];
      tasks.forEach((taskItem) => {
        if (!matchesDevice(taskItem)) {
          return;
        }

        const optionId = String(taskItem.pk_id || "");
        const optionName = String(taskItem.task_name || "").trim();
        if (!optionId || !optionName) {
          return;
        }

        if (!taskMap.has(optionId)) {
          taskMap.set(optionId, {
            id: optionId,
            name: optionName,
          });
        }
      });
    });

    return Array.from(taskMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [
    editDraft.device_name,
    editDraft.service_type_name,
    isCreateMode,
    job?.device_id,
    job?.device_name,
    job?.service_type_id,
    job?.service_type_name,
    serviceOptions,
    servicesByType,
    tasksByService,
  ]);

  const selectMenuProps = useMemo(
    () => ({
      MenuProps: {
        sx: { zIndex: 6001 },
        PaperProps: {
          sx: { zIndex: 6001 },
        },
      },
    }),
    [],
  );

  const tooltipSlotProps = useMemo(
    () => ({
      popper: {
        sx: { zIndex: 4000 },
      },
    }),
    [],
  );

  const edited = useMemo(() => {
    if (isCreateMode) {
      return !shallowEqual(createDraft, createOriginal);
    }
    return !shallowEqual(editDraft, editOriginal);
  }, [createDraft, createOriginal, editDraft, editOriginal, isCreateMode]);

  const taskSummary = useMemo(
    () => buildTaskSummary(isCreateMode ? createDraft.tasks : editDraft.tasks),
    [createDraft.tasks, editDraft.tasks, isCreateMode],
  );

  const taskEditorOpen = taskEditorIndex !== null;
  const activeEditorTask = useMemo(() => {
    if (taskEditorIndex === null) {
      return null;
    }

    const taskList = isCreateMode ? createDraft.tasks : editDraft.tasks;
    return taskList?.[taskEditorIndex] || null;
  }, [createDraft.tasks, editDraft.tasks, isCreateMode, taskEditorIndex]);

  const openTaskEditor = (index) => setTaskEditorIndex(index);
  const closeTaskEditor = () => setTaskEditorIndex(null);

  useEffect(() => {
    if (reportedDirtyRef.current === edited) {
      return;
    }

    reportedDirtyRef.current = edited;

    if (onDirtyChange) {
      onDirtyChange(edited);
    }
  }, [edited, onDirtyChange]);

  const handleEditField = (key, value) => {
    setEditDraft((prev) => ({ ...prev, [key]: value }));
  };

  const handleEditTaskField = (index, key, value) => {
    setEditDraft((prev) => ({
      ...prev,
      tasks: prev.tasks.map((task, taskIndex) =>
        taskIndex === index ? { ...task, [key]: value } : task,
      ),
    }));
  };

  const handleEditTaskSelection = (index, taskId) => {
    const selected = editTaskOptions.find(
      (taskOption) => String(taskOption.id) === String(taskId),
    );

    setEditDraft((prev) => ({
      ...prev,
      tasks: prev.tasks.map((task, taskIndex) =>
        taskIndex === index
          ? {
              ...task,
              task_id: String(taskId || ""),
              task_name: selected?.name || task.task_name,
            }
          : task,
      ),
    }));
  };

  const handleCreateField = (key, value) => {
    setCreateDraft((prev) => {
      const next = { ...prev, [key]: value };

      if (key === "customer_id") {
        next.location_id = "";
        next.device_id = "";
        next.tasks = [createNewTaskDraft()];
      }

      if (key === "location_id") {
        next.device_id = "";
        next.tasks = [createNewTaskDraft()];
      }

      if (key === "device_id") {
        next.tasks = [createNewTaskDraft()];
      }

      return next;
    });
  };

  const handleCreateTaskField = (index, key, value) => {
    setCreateDraft((prev) => {
      const tasks = prev.tasks.map((task, taskIndex) =>
        taskIndex === index ? { ...task, [key]: value } : task,
      );

      if (key === "service_id") {
        tasks[index].task_id = "";
        tasks[index].estimated_duration = "";
        tasks[index].description = "";
        tasks[index].task_name = "";
      }

      if (key === "task_id" && value) {
        const selectedTask = tasksByService[
          tasks[index].service_id
        ]?.task?.find((item) => String(item.pk_id) === String(value));

        if (selectedTask) {
          tasks[index].task_name = selectedTask.task_name || "";
          tasks[index].estimated_duration = String(
            (selectedTask.estimated_duration_minutes || 0) / 60,
          );
          tasks[index].description = selectedTask.task_description || "";
          tasks[index].status = tasks[index].status || "pending";
        }
      }

      return { ...prev, tasks };
    });
  };

  const handleCancel = () => {
    setErrorMessage("");
    if (isCreateMode) {
      setCreateDraft(createOriginal);
      if (onRequestClose) onRequestClose();
      return;
    }
    setEditDraft(editOriginal);
  };

  const handleSave = async () => {
    try {
      setSaveBusy(true);
      setErrorMessage("");

      if (isCreateMode) {
        if (!onCreateJob) return;

        if (
          !createDraft.customer_id ||
          !createDraft.service_type_id ||
          !createDraft.location_id
        ) {
          setErrorMessage("Customer, service type, and location are required");
          return;
        }

        await onCreateJob(createDraft);
        if (onRequestClose) onRequestClose();
        return;
      }

      if (!job || !onUpdateJob) {
        return;
      }

      if (!isJobStatusAllowed(editDraft.status, hasAssignedTechnician)) {
        setErrorMessage(
          hasAssignedTechnician
            ? "An assigned job cannot be placed in pending"
            : "An unassigned job must remain in pending",
        );
        return;
      }

      const normalizedTasks = (editDraft.tasks || [])
        .filter((task) => Boolean(task.task_id))
        .map((task) => ({
          task_id: task.task_id,
          task_name: task.task_name?.trim() || "Task",
          status: task.status || "pending",
          task_notes: task.notes?.trim() || null,
          parts_used: task.parts_used?.trim() || null,
          failure_reason:
            String(task.status || "pending").toLowerCase() === "failed"
              ? task.failure_reason?.trim() || null
              : null,
          time_spent_minutes:
            task.time_spent_minutes === ""
              ? null
              : Number(task.time_spent_minutes) || 0,
        }));

      const payload = {
        job_number: editDraft.job_number.trim(),
        customer_name: editDraft.customer_name.trim(),
        status: editDraft.status,
        priority: editDraft.priority,
        assigned_technician_id: assignedTechnicianId
          ? Number(assignedTechnicianId)
          : null,
        assigned_technician_name:
          technicianOptions.find(
            (option) => String(option.value) === String(assignedTechnicianId),
          )?.label || assignedTechnicianName,
        service_type_name: editDraft.service_type_name.trim(),
        device_name: editDraft.device_name.trim(),
        scheduled_start: editDraft.scheduled_start
          ? new Date(editDraft.scheduled_start).toISOString()
          : null,
        scheduled_end: editDraft.scheduled_end
          ? new Date(editDraft.scheduled_end).toISOString()
          : null,
        external_notes: editDraft.external_notes.trim(),
        ...(isAdminUser
          ? { internal_notes: editDraft.internal_notes.trim() }
          : {}),
        cancellation_reason: editDraft.cancellation_reason.trim(),
        tasks: normalizedTasks,
        total_job_tasks: normalizedTasks.length,
      };

      await onUpdateJob(job.pk_id, payload);
      const next = createEditJobDraft(
        { ...job, ...payload },
        technicianOptions,
      );
      setEditOriginal(next);
      setEditDraft(next);
    } catch (err) {
      setErrorMessage(err?.message || "Failed to save changes");
    } finally {
      setSaveBusy(false);
    }
  };

  const readOnly = !isCreateMode && !editable;
  const canAddCreateTasks = Boolean(createDraft.location_id);

  if (!isCreateMode && !job) {
    return (
      <Typography variant="body2" color="text.secondary">
        Select a job from the queue to inspect.
      </Typography>
    );
  }

  return (
    <Stack
      spacing={1.5}
      sx={{
        "& .MuiTextField-root": {
          "& .MuiInputLabel-root": {
            fontSize: 12,
          },
          "& .MuiInputBase-root": {
            minHeight: 34,
            fontSize: 12,
          },
          "& .MuiInputBase-input": {
            py: 0.65,
          },
        },
      }}
    >
      {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}

      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        useFlexGap
        flexWrap="wrap"
      >
        <Chip
          size="small"
          label={
            isCreateMode ? "Create Mode" : readOnly ? "Locked" : "Unlocked"
          }
          color={isCreateMode ? "primary" : readOnly ? "default" : "success"}
        />
        {!isCreateMode && edited ? (
          <Chip size="small" color="warning" label="Edited" />
        ) : null}
        {!isCreateMode ? (
          <Stack spacing={0.15} alignItems="flex-end" sx={{ ml: "auto" }}>
            <Typography variant="caption" color="text.secondary">
              {job?.generation_source} creation by{" "}
              {job?.created_by_username || job?.created_by || "System"}
              {` on ${formatAuditDateTime(job?.created_at)}`}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Last updated {formatAuditDateTime(job?.updated_at)}
            </Typography>
          </Stack>
        ) : null}
      </Stack>

      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        <Chip
          size="small"
          variant="outlined"
          label={`Tasks ${taskSummary.total}`}
        />
        <Chip size="small" label={`Pending ${taskSummary.pending}`} />
        <Chip
          size="small"
          color="info"
          label={`In Progress ${taskSummary.in_progress}`}
        />
        <Chip
          size="small"
          color="success"
          label={`Completed ${taskSummary.completed}`}
        />
        <Chip
          size="small"
          color="error"
          label={`Failed ${taskSummary.failed}`}
        />
        {taskSummary.other > 0 ? (
          <Chip
            size="small"
            variant="outlined"
            label={`Other ${taskSummary.other}`}
          />
        ) : null}
      </Stack>

      {isCreateMode ? (
        <>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            {!isCustomerWithIdUser ? (
              <>
                {!customerPkId && (
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Customer"
                    value={createDraft.customer_id}
                    onChange={(event) =>
                      handleCreateField("customer_id", event.target.value)
                    }
                    SelectProps={selectMenuProps}
                  >
                    <MenuItem value="">Select Customer</MenuItem>
                    {customers.map((customer) => (
                      <MenuItem key={customer.pk_id} value={customer.pk_id}>
                        {customer.uk_name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Location"
                  InputLabelProps={{ shrink: true }}
                  value={createDraft.location_id}
                  onChange={(event) =>
                    handleCreateField("location_id", event.target.value)
                  }
                  disabled={!createDraft.customer_id}
                  SelectProps={selectMenuProps}
                >
                  <MenuItem value="">Select Location</MenuItem>
                  {locationOptions.map((location) => (
                    <MenuItem
                      key={location.pk_id}
                      value={String(location.pk_id)}
                    >
                      {location.location_name ||
                        location.uk_name ||
                        `Location ${location.pk_id}`}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Service Type"
                  InputLabelProps={{ shrink: true }}
                  value={createDraft.service_type_id}
                  onChange={(event) =>
                    handleCreateField("service_type_id", event.target.value)
                  }
                  SelectProps={selectMenuProps}
                >
                  <MenuItem value="">Select Service Type</MenuItem>
                  {serviceOptions.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </TextField>
              </>
            ) : null}

            <TextField
              select
              fullWidth
              size="small"
              variant="outlined"
              label={!isCustomerWithIdUser ? "Device" : "Room"}
              InputLabelProps={{ shrink: true }}
              value={createDraft.device_id}
              onChange={(event) =>
                handleCreateField("device_id", event.target.value)
              }
              disabled={!createDraft.customer_id || !createDraft.location_id}
              SelectProps={selectMenuProps}
            >
              <MenuItem value="">
                {!isCustomerWithIdUser ? "Select Device" : "Select Room"}
              </MenuItem>
              {filteredCreateDevices.map((device) => (
                <MenuItem key={device.pk_id} value={device.pk_id}>
                  {!isCustomerWithIdUser
                    ? device.deviceName ||
                      device.name ||
                      `Device ${device.pk_id}`
                    : device.roomName || device.name || `Room ${device.pk_id}`}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <TextField
              fullWidth
              size="small"
              type="datetime-local"
              label="Scheduled Start"
              value={createDraft.scheduled_start}
              onChange={(event) =>
                handleCreateField("scheduled_start", event.target.value)
              }
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              size="small"
              type="datetime-local"
              label="Scheduled End"
              value={createDraft.scheduled_end}
              onChange={(event) =>
                handleCreateField("scheduled_end", event.target.value)
              }
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              select
              fullWidth
              size="small"
              label="Status"
              disabled={true}
              value={createDraft.status}
              onChange={(event) =>
                handleCreateField("status", event.target.value)
              }
              SelectProps={selectMenuProps}
            >
              {JOB_STATUS_OPTIONS.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              fullWidth
              size="small"
              label="Priority"
              value={createDraft.priority}
              onChange={(event) =>
                handleCreateField("priority", event.target.value)
              }
              SelectProps={selectMenuProps}
            >
              {JOB_PRIORITY_OPTIONS.map((priority) => (
                <MenuItem key={priority} value={priority}>
                  {priority}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <Box sx={{ border: "1px solid", borderColor: "divider" }}>
            <Tabs
              value={notesTab}
              onChange={(_, value) => setNotesTab(value)}
              variant="standard"
              sx={{
                minHeight: 36,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Tab
                value="external"
                label={isAdminUser ? "External Notes" : "Notes"}
                sx={{ minHeight: 36, py: 0.5 }}
              />
              {isAdminUser ? (
                <Tab
                  value="internal"
                  label="Internal Notes"
                  sx={{ minHeight: 36, py: 0.5 }}
                />
              ) : null}
            </Tabs>
            <Box sx={{ p: 1 }}>
              {notesTab === "internal" && isAdminUser ? (
                <TextField
                  variant="standard"
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  sx={{ padding: 1 }}
                  InputProps={{
                    disableUnderline: true,
                  }}
                  value={createDraft.internal_notes}
                  onChange={(event) =>
                    handleCreateField("internal_notes", event.target.value)
                  }
                />
              ) : (
                <TextField
                  variant="standard"
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  sx={{ padding: 1 }}
                  InputProps={{
                    disableUnderline: true,
                  }}
                  value={createDraft.external_notes}
                  onChange={(event) =>
                    handleCreateField("external_notes", event.target.value)
                  }
                />
              )}
            </Box>
          </Box>

          <Divider />

          <Stack spacing={1}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="subtitle2">Tasks</Typography>
              <Tooltip title="Add task" slotProps={tooltipSlotProps}>
                <span>
                  <IconButton
                    size="small"
                    disabled={readOnly || !canAddCreateTasks}
                    onClick={() =>
                      setCreateDraft((prev) => ({
                        ...prev,
                        tasks: [...prev.tasks, createNewTaskDraft()],
                      }))
                    }
                  >
                    <AddCircleOutlineOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
            {createDraft.tasks.map((task, index) => (
              <Stack
                key={`new-task-${index}`}
                direction={{ xs: "column", md: "row" }}
                spacing={1}
              >
                {(() => {
                  const currentServiceTasks =
                    tasksByService[String(task.service_id)]?.task || [];
                  const selectedDevice = filteredCreateDevices.find(
                    (device) =>
                      String(device.pk_id) === String(createDraft.device_id),
                  );
                  const selectedDeviceId = String(
                    selectedDevice?.pk_id || createDraft.device_id,
                  )
                    .trim()
                    .toLowerCase();
                  const selectedDeviceName = String(
                    selectedDevice?.deviceName || selectedDevice?.name || "",
                  )
                    .trim()
                    .toLowerCase();

                  const hasTaskOptions = currentServiceTasks.some(
                    (taskItem) => {
                      const taskDeviceIds = [
                        ...(Array.isArray(taskItem?.device_ids)
                          ? taskItem.device_ids
                          : []),
                        ...(Array.isArray(taskItem?.allowed_device_ids)
                          ? taskItem.allowed_device_ids
                          : []),
                        ...(Array.isArray(taskItem?.compatible_device_ids)
                          ? taskItem.compatible_device_ids
                          : []),
                        taskItem?.device_id,
                      ]
                        .filter(Boolean)
                        .map((value) => String(value).trim().toLowerCase());

                      const taskDeviceNames = [
                        ...(Array.isArray(taskItem?.device_names)
                          ? taskItem.device_names
                          : []),
                        taskItem?.device_name,
                      ]
                        .filter(Boolean)
                        .map((value) => String(value).trim().toLowerCase());

                      const hasDeviceConstraint =
                        taskDeviceIds.length > 0 || taskDeviceNames.length > 0;

                      if (!hasDeviceConstraint) {
                        return true;
                      }

                      return (
                        (selectedDeviceId &&
                          taskDeviceIds.includes(selectedDeviceId)) ||
                        (selectedDeviceName &&
                          taskDeviceNames.includes(selectedDeviceName))
                      );
                    },
                  );

                  return null;
                })()}
                <TextField
                  select
                  size="small"
                  label="Service"
                  value={task.service_id}
                  onChange={(event) =>
                    handleCreateTaskField(
                      index,
                      "service_id",
                      event.target.value,
                    )
                  }
                  sx={{ minWidth: 180 }}
                  SelectProps={selectMenuProps}
                >
                  <MenuItem value="">Select Service</MenuItem>
                  {servicesByType[createDraft.service_type_id]?.services?.map(
                    (service) => (
                      <MenuItem key={service.pk_id} value={service.pk_id}>
                        {service.service_name}
                      </MenuItem>
                    ),
                  )}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Task"
                  value={task.task_id}
                  onChange={(event) =>
                    handleCreateTaskField(index, "task_id", event.target.value)
                  }
                  sx={{ minWidth: 180 }}
                  disabled={!task.service_id}
                  SelectProps={selectMenuProps}
                >
                  <MenuItem value="">Select Task</MenuItem>
                  {(tasksByService[String(task.service_id)]?.task || [])
                    .filter((taskItem) => {
                      const selectedDevice = filteredCreateDevices.find(
                        (device) =>
                          String(device.pk_id) ===
                          String(createDraft.device_id),
                      );
                      const selectedDeviceId = String(
                        selectedDevice?.pk_id || createDraft.device_id,
                      )
                        .trim()
                        .toLowerCase();
                      const selectedDeviceName = String(
                        selectedDevice?.deviceName ||
                          selectedDevice?.name ||
                          "",
                      )
                        .trim()
                        .toLowerCase();

                      const taskDeviceIds = [
                        ...(Array.isArray(taskItem?.device_ids)
                          ? taskItem.device_ids
                          : []),
                        ...(Array.isArray(taskItem?.allowed_device_ids)
                          ? taskItem.allowed_device_ids
                          : []),
                        ...(Array.isArray(taskItem?.compatible_device_ids)
                          ? taskItem.compatible_device_ids
                          : []),
                        taskItem?.device_id,
                      ]
                        .filter(Boolean)
                        .map((value) => String(value).trim().toLowerCase());

                      const taskDeviceNames = [
                        ...(Array.isArray(taskItem?.device_names)
                          ? taskItem.device_names
                          : []),
                        taskItem?.device_name,
                      ]
                        .filter(Boolean)
                        .map((value) => String(value).trim().toLowerCase());

                      const hasDeviceConstraint =
                        taskDeviceIds.length > 0 || taskDeviceNames.length > 0;

                      if (!hasDeviceConstraint) {
                        return true;
                      }

                      return (
                        (selectedDeviceId &&
                          taskDeviceIds.includes(selectedDeviceId)) ||
                        (selectedDeviceName &&
                          taskDeviceNames.includes(selectedDeviceName))
                      );
                    })
                    .map((taskItem) => (
                      <MenuItem key={taskItem.pk_id} value={taskItem.pk_id}>
                        {taskItem.task_name}
                      </MenuItem>
                    ))}
                </TextField>
                <Tooltip title="Remove task" slotProps={tooltipSlotProps}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() =>
                        setCreateDraft((prev) => ({
                          ...prev,
                          tasks:
                            prev.tasks.length <= 1
                              ? prev.tasks
                              : prev.tasks.filter(
                                  (_, taskIdx) => taskIdx !== index,
                                ),
                        }))
                      }
                      disabled={createDraft.tasks.length <= 1}
                    >
                      <RemoveCircleOutlineOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip
                  title={readOnly ? "View task" : "Edit task"}
                  slotProps={tooltipSlotProps}
                >
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => openTaskEditor(index)}
                    >
                      {readOnly ? (
                        <VisibilityOutlinedIcon fontSize="small" />
                      ) : (
                        <EditOutlinedIcon fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            ))}
          </Stack>
        </>
      ) : (
        <>
          {!isCustomerWithIdUser && (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <TextField
                label="Job Number"
                value={editDraft.job_number}
                onChange={(event) =>
                  handleEditField("job_number", event.target.value)
                }
                fullWidth
                size="small"
                disabled
              />
              <TextField
                label="Customer"
                value={editDraft.customer_name}
                onChange={(event) =>
                  handleEditField("customer_name", event.target.value)
                }
                fullWidth
                size="small"
                disabled
              />
            </Stack>
          )}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <TextField
              label={!isCustomerWithIdUser ? "Device" : "Room"}
              value={
                !isCustomerWithIdUser ? editDraft.device_name : job.room_name
              }
              InputLabelProps={{ shrink: true }}
              onChange={(event) =>
                handleEditField(
                  !isCustomerWithIdUser ? "device_name" : "room_name",
                  event.target.value,
                )
              }
              fullWidth
              size="small"
              disabled
            />
            <TextField
              label="Service"
              value={editDraft.service_type_name}
              onChange={(event) =>
                handleEditField("service_type_name", event.target.value)
              }
              fullWidth
              size="small"
              disabled
            />
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <TextField
              select
              label="Status"
              value={editDraft.status}
              onChange={(event) =>
                handleEditField("status", event.target.value)
              }
              fullWidth
              size="small"
              disabled={readOnly}
              SelectProps={selectMenuProps}
            >
              {JOB_STATUS_OPTIONS.map((status) => (
                <MenuItem
                  key={status}
                  value={status}
                  disabled={!isJobStatusAllowed(status, hasAssignedTechnician)}
                >
                  {status}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Priority"
              value={editDraft.priority}
              onChange={(event) =>
                handleEditField("priority", event.target.value)
              }
              fullWidth
              size="small"
              disabled={readOnly}
              SelectProps={selectMenuProps}
            >
              {JOB_PRIORITY_OPTIONS.map((priority) => (
                <MenuItem key={priority} value={priority}>
                  {priority}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Technician"
              value={
                !editDraft.assigned_technician_name?.trim()
                  ? "Unassigned"
                  : editDraft.assigned_technician_name
              }
              fullWidth
              size="small"
              disabled={true}
            ></TextField>
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <TextField
              type="datetime-local"
              label="Scheduled Start"
              value={editDraft.scheduled_start}
              onChange={(event) =>
                handleEditField("scheduled_start", event.target.value)
              }
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
              disabled={readOnly}
            />
            <TextField
              type="datetime-local"
              label="Scheduled End"
              value={editDraft.scheduled_end}
              onChange={(event) =>
                handleEditField("scheduled_end", event.target.value)
              }
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
              disabled={readOnly}
            />
            <TextField
              label="Actual Start"
              value={job.actual_start || ""}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
              disabled
            />

            <TextField
              label="Actual End"
              value={job.actual_end || ""}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
              disabled
            />
          </Stack>
          <Box sx={{ border: "1px solid", borderColor: "divider" }}>
            <Tabs
              value={notesTab}
              onChange={(_, value) => setNotesTab(value)}
              sx={{
                minHeight: 36,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Tab
                value="external"
                label={isAdminUser ? "External Notes" : "Notes"}
                sx={{ minHeight: 36, py: 0.5 }}
              />
              {isAdminUser ? (
                <Tab
                  value="internal"
                  label="Internal Notes"
                  sx={{ minHeight: 36, py: 0.5 }}
                />
              ) : null}
              <Tab
                value="cancellation"
                label="Failure Reason"
                sx={{ minHeight: 36, py: 0.5 }}
              />
            </Tabs>
            <Box sx={{ p: 1 }}>
              {notesTab === "cancellation" ? (
                <TextField
                  variant="standard"
                  value={editDraft.cancellation_reason}
                  onChange={(event) =>
                    handleEditField("cancellation_reason", event.target.value)
                  }
                  multiline
                  minRows={2}
                  InputProps={{ disableUnderline: true }}
                  fullWidth
                  size="small"
                  disabled={readOnly}
                />
              ) : notesTab === "internal" && isAdminUser ? (
                <TextField
                  variant="standard"
                  value={editDraft.internal_notes}
                  onChange={(event) =>
                    handleEditField("internal_notes", event.target.value)
                  }
                  multiline
                  minRows={2}
                  InputProps={{ disableUnderline: true }}
                  fullWidth
                  size="small"
                  disabled={readOnly}
                />
              ) : (
                <TextField
                  variant="standard"
                  value={editDraft.external_notes}
                  onChange={(event) =>
                    handleEditField("external_notes", event.target.value)
                  }
                  multiline
                  minRows={2}
                  InputProps={{ disableUnderline: true }}
                  fullWidth
                  size="small"
                  disabled={readOnly}
                />
              )}
            </Box>
          </Box>
          <Divider />
          <Stack spacing={1}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="subtitle2">Tasks</Typography>
              <Tooltip title="Add task" slotProps={tooltipSlotProps}>
                <span>
                  <IconButton
                    size="small"
                    disabled={readOnly}
                    onClick={() =>
                      setEditDraft((prev) => ({
                        ...prev,
                        tasks: [...prev.tasks, createNewTaskDraft()],
                      }))
                    }
                  >
                    <AddCircleOutlineOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
            {editDraft.tasks.map((task, index) => (
              <Stack
                key={`edit-task-${index}`}
                direction={{ xs: "column", md: "row" }}
                spacing={1}
              >
                {task.is_new ? (
                  <TextField
                    select
                    label="Task Name"
                    value={task.task_id ? String(task.task_id) : ""}
                    onChange={(event) =>
                      handleEditTaskSelection(index, event.target.value)
                    }
                    size="small"
                    fullWidth
                    disabled={readOnly}
                    SelectProps={selectMenuProps}
                  >
                    <MenuItem value="">
                      {task.task_name
                        ? `Current: ${task.task_name}`
                        : "Select task"}
                    </MenuItem>
                    {editTaskOptions.map((taskOption) => (
                      <MenuItem key={taskOption.id} value={taskOption.id}>
                        {taskOption.name}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <TextField
                    label="Task Name"
                    value={task.task_name || ""}
                    size="small"
                    fullWidth
                    disabled
                  />
                )}
                <TextField
                  select
                  label="Task Status"
                  value={task.status}
                  onChange={(event) =>
                    handleEditTaskField(index, "status", event.target.value)
                  }
                  size="small"
                  sx={{ minWidth: 140 }}
                  disabled={readOnly}
                  SelectProps={selectMenuProps}
                >
                  {TASK_STATUS_OPTIONS.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  type="number"
                  label="Minutes"
                  value={task.time_spent_minutes}
                  onChange={(event) =>
                    handleEditTaskField(
                      index,
                      "time_spent_minutes",
                      event.target.value,
                    )
                  }
                  size="small"
                  sx={{ minWidth: 120 }}
                  disabled={readOnly}
                />
                <Tooltip title="Remove task" slotProps={tooltipSlotProps}>
                  <span>
                    <IconButton
                      size="small"
                      disabled={readOnly || editDraft.tasks.length <= 1}
                      onClick={() =>
                        setEditDraft((prev) => ({
                          ...prev,
                          tasks:
                            prev.tasks.length <= 1
                              ? prev.tasks
                              : prev.tasks.filter(
                                  (_, taskIdx) => taskIdx !== index,
                                ),
                        }))
                      }
                    >
                      <RemoveCircleOutlineOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip
                  title={readOnly ? "View task" : "Edit task"}
                  slotProps={tooltipSlotProps}
                >
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => openTaskEditor(index)}
                    >
                      {readOnly ? (
                        <VisibilityOutlinedIcon fontSize="small" />
                      ) : (
                        <EditOutlinedIcon fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            ))}
          </Stack>
        </>
      )}

      <Divider />

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="caption" color="text.secondary">
          {edited ? "Edited" : "No unsaved changes"}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="text"
            onClick={handleCancel}
            disabled={saveBusy}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleSave}
            disabled={!edited || saveBusy}
          >
            Save
          </Button>
        </Stack>
      </Stack>

      <Dialog
        open={taskEditorOpen}
        onClose={closeTaskEditor}
        fullWidth
        maxWidth="sm"
        sx={{ zIndex: 5000 }}
        BackdropProps={{ sx: { zIndex: 4999 } }}
        PaperProps={{ sx: { zIndex: 5000 } }}
      >
        <DialogTitle>Task Editor</DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          {activeEditorTask ? (
            <Stack spacing={1.25} sx={{ mt: 1 }}>
              {isCreateMode || activeEditorTask.is_new ? (
                <TextField
                  select
                  size="small"
                  label="Task Name"
                  value={
                    activeEditorTask.task_id
                      ? String(activeEditorTask.task_id)
                      : ""
                  }
                  onChange={(event) => {
                    if (taskEditorIndex === null) return;
                    handleEditTaskSelection(
                      taskEditorIndex,
                      event.target.value,
                    );
                  }}
                  fullWidth
                  SelectProps={selectMenuProps}
                >
                  <MenuItem value="">
                    {activeEditorTask.task_name
                      ? `Current: ${activeEditorTask.task_name}`
                      : "Select task"}
                  </MenuItem>
                  {editTaskOptions.map((taskOption) => (
                    <MenuItem key={taskOption.id} value={taskOption.id}>
                      {taskOption.name}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <TextField
                  size="small"
                  label="Task Name"
                  value={activeEditorTask.task_name || ""}
                  disabled
                  fullWidth
                />
              )}
              <TextField
                select
                size="small"
                label="Task Status"
                value={activeEditorTask.status || "pending"}
                onChange={(event) => {
                  if (taskEditorIndex === null) return;
                  if (isCreateMode) {
                    handleCreateTaskField(
                      taskEditorIndex,
                      "status",
                      event.target.value,
                    );
                  } else {
                    handleEditTaskField(
                      taskEditorIndex,
                      "status",
                      event.target.value,
                    );
                  }
                }}
                disabled={!isCreateMode && readOnly}
                SelectProps={selectMenuProps}
                fullWidth
              >
                {TASK_STATUS_OPTIONS.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                type="number"
                size="small"
                label="Time Spent Minutes"
                value={activeEditorTask.time_spent_minutes ?? ""}
                onChange={(event) => {
                  if (taskEditorIndex === null) return;
                  if (isCreateMode) {
                    handleCreateTaskField(
                      taskEditorIndex,
                      "time_spent_minutes",
                      event.target.value,
                    );
                  } else {
                    handleEditTaskField(
                      taskEditorIndex,
                      "time_spent_minutes",
                      event.target.value,
                    );
                  }
                }}
                disabled={!isCreateMode && readOnly}
                fullWidth
              />
              <TextField
                size="small"
                label="Task Notes"
                value={activeEditorTask.notes || ""}
                onChange={(event) => {
                  if (taskEditorIndex === null) return;
                  if (isCreateMode) {
                    handleCreateTaskField(
                      taskEditorIndex,
                      "notes",
                      event.target.value,
                    );
                  } else {
                    handleEditTaskField(
                      taskEditorIndex,
                      "notes",
                      event.target.value,
                    );
                  }
                }}
                disabled={!isCreateMode && readOnly}
                multiline
                minRows={2}
                fullWidth
              />
              <TextField
                size="small"
                label="Parts Used"
                value={activeEditorTask.parts_used || ""}
                onChange={(event) => {
                  if (taskEditorIndex === null) return;
                  if (isCreateMode) {
                    handleCreateTaskField(
                      taskEditorIndex,
                      "parts_used",
                      event.target.value,
                    );
                  } else {
                    handleEditTaskField(
                      taskEditorIndex,
                      "parts_used",
                      event.target.value,
                    );
                  }
                }}
                disabled={!isCreateMode && readOnly}
                multiline
                minRows={2}
                fullWidth
              />
              {String(activeEditorTask.status || "pending").toLowerCase() ===
              "failed" ? (
                <TextField
                  size="small"
                  label="Failure Reason"
                  value={activeEditorTask.failure_reason || ""}
                  onChange={(event) => {
                    if (taskEditorIndex === null) return;
                    if (isCreateMode) {
                      handleCreateTaskField(
                        taskEditorIndex,
                        "failure_reason",
                        event.target.value,
                      );
                    } else {
                      handleEditTaskField(
                        taskEditorIndex,
                        "failure_reason",
                        event.target.value,
                      );
                    }
                  }}
                  disabled={!isCreateMode && readOnly}
                  multiline
                  minRows={2}
                  fullWidth
                />
              ) : null}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeTaskEditor}>Close</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
